import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { AuthUser, AuthProvider as AuthProviderName } from '../types';
import { login as apiLogin, entraLogin as apiEntraLogin, setLogoutCallback } from '../services/api';
import { getMsalInstance, loginRequest } from '../config/msal';
import { getErrorMessage } from '../utils/errors';
import { features } from '../config/features';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  authProvider: AuthProviderName | null;
  entraError: string | null;
  login: (username: string, password: string) => Promise<void>;
  loginWithEntra: () => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isTrackOperator: boolean;
  isSanctioningBody: boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  authProvider: null,
  entraError: null,
  login: async () => {},
  loginWithEntra: async () => {},
  logout: () => {},
  isAuthenticated: false,
  isAdmin: false,
  isTrackOperator: false,
  isSanctioningBody: false,
  hasRole: () => false,
});

export const useAuth = () => useContext(AuthContext);

/**
 * True when the current URL carries an authorization-code response from Entra.
 *
 * MSAL uses `response_mode=fragment` for a SPA registration, so the code comes
 * back as `#code=...`; `error=` covers the rejection case, which must also be
 * read before anything can rewrite the URL.
 */
const hasRedirectResponse = (): boolean =>
  /(^|[#?&])(code|error)=/.test(window.location.hash + window.location.search);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const stored = sessionStorage.getItem('user');
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      sessionStorage.removeItem('user');
      return null;
    }
  });
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem('token'));
  const [authProvider, setAuthProvider] = useState<AuthProviderName | null>(
    () => (sessionStorage.getItem('authProvider') as AuthProviderName | null) ?? null
  );
  const [entraError, setEntraError] = useState<string | null>(null);

  const applySession = useCallback(
    (response: { token: string; user: AuthUser }, provider: AuthProviderName) => {
      setUser(response.user);
      setToken(response.token);
      setAuthProvider(provider);
      sessionStorage.setItem('user', JSON.stringify(response.user));
      sessionStorage.setItem('token', response.token);
      sessionStorage.setItem('authProvider', provider);
    },
    []
  );

  const login = useCallback(
    async (username: string, password: string) => {
      applySession(await apiLogin(username, password), 'local');
    },
    [applySession]
  );

  // Redirect rather than popup: popup blockers on track-side machines are a
  // predictable support burden, and redirect is more robust behind Caddy.
  const loginWithEntra = useCallback(async () => {
    setEntraError(null);
    const msal = getMsalInstance();
    await msal.initialize();
    await msal.loginRedirect(loginRequest);
  }, []);

  // Guards against React 19 StrictMode's dev-only double-invoke of effects
  // (run, clean up, run again). A ref survives the remount because it's the
  // same component instance, so the redirect is only ever handed to MSAL
  // once per page load — MSAL clears the redirect state after the first
  // handleRedirectPromise() call, so a second real invocation would resolve
  // null and silently drop the exchange.
  const redirectHandled = useRef(false);

  // Completes the redirect: MSAL hands back the ID token, which is exchanged
  // for the same 8h app JWT the password path returns.
  //
  // Children are withheld until this settles, and that is load-bearing rather
  // than cosmetic. MSAL returns from Entra to `/#code=...`, carrying the
  // authorization code in the URL fragment. This effect is async — it awaits
  // msal.initialize() before it can read that fragment — but React renders
  // children synchronously on the same mount, and AuthProvider wraps
  // BrowserRouter (App.tsx). So the router would mount, ProtectedRoute would
  // see an unauthenticated user, and <Navigate to="/login" replace /> would
  // rewrite the URL and DISCARD the fragment before handleRedirectPromise()
  // ever read it. It then finds no response, resolves null, and the exchange
  // never happens — no request, no error, just a bounce back to the login
  // page. That shipped, and cost a deployment to diagnose.
  //
  // Children are withheld only when the URL actually carries a redirect
  // response: gating every page load would blank the first frame of every
  // visit for a race that exists only on the way back from Entra.
  const [initializing, setInitializing] = useState(
    () => features.entraAuth && hasRedirectResponse()
  );

  useEffect(() => {
    // Nothing to complete when Entra is not configured in this build, and no
    // reason to construct an MSAL client that will never be used.
    if (!features.entraAuth) return;
    if (redirectHandled.current) return;
    redirectHandled.current = true;
    (async () => {
      try {
        const msal = getMsalInstance();
        await msal.initialize();
        const result = await msal.handleRedirectPromise();
        if (result?.idToken) {
          applySession(await apiEntraLogin(result.idToken), 'entra');
        }
      } catch (error) {
        console.error('Entra sign-in failed', error);
        setEntraError(getErrorMessage(error));
      } finally {
        // In a finally so a failure here can never leave the app permanently
        // blank.
        setInitializing(false);
      }
    })();
  }, [applySession]);

  const clearSession = useCallback(() => {
    setUser(null);
    setToken(null);
    setAuthProvider(null);
    setEntraError(null);
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('authProvider');
  }, []);

  const logout = useCallback(() => {
    const wasEntra = authProvider === 'entra';
    clearSession();
    // Without this, the next person on a shared machine clicks "Sign in with
    // Microsoft" and is silently signed straight back in as the previous user.
    if (wasEntra) {
      getMsalInstance().logoutRedirect().catch((error) => {
        console.error('Entra sign-out failed', error);
      });
    }
  }, [authProvider, clearSession]);

  // Registered as `logout`, not `clearSession`: the axios interceptor calls this
  // on every 401, and token expiry (an 8h JWT on an all-day event machine) is at
  // least as likely a route here as an explicit click on "Log out". If this only
  // cleared local state, the Entra IdP session cookie would survive an expiry-
  // triggered "logout", and the next person on a shared machine who clicks
  // "Sign in with Microsoft" would be silently SSO'd into the previous
  // operator's session. Re-registered whenever `logout` changes so the callback
  // always closes over the current `authProvider`.
  useEffect(() => {
    setLogoutCallback(() => {
      logout();
    });
  }, [logout]);

  const roles = user?.roles || [];
  const isAdmin = roles.includes('ADMINISTRATOR');
  const isTrackOperator = roles.includes('TRACK_OPERATOR');
  const isSanctioningBody = roles.includes('SANCTIONING_BODY');
  const hasRole = useCallback((role: string) => roles.includes(role), [roles]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      authProvider,
      entraError,
      login,
      loginWithEntra,
      logout,
      isAuthenticated: !!token,
      isAdmin,
      isTrackOperator,
      isSanctioningBody,
      hasRole,
    }}>
      {initializing ? null : children}
    </AuthContext.Provider>
  );
};
